function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { jsx } from 'panel';
import type { VNode } from 'snabbdom';
import isFunction from 'lodash/isFunction';
import { SvgIconEl } from 'common/components/svg-icon';
import { SpinnerEl } from 'common/components/spinner';
import { ExperimentalTooltipEl } from 'common/components/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { unwrapSection } from 'common/components/items-menu/utils';
import { LocalizationStore } from 'common/i18n/store';
import { ItemAndEventHandler, ItemHandler, SectionItemContextPanelTransform, SelectedTheme, WrappedSection, WrappedSectionItem } from '../types';
const TooltipWrapper = (_ref: {
  item: WrappedSectionItem;
}, sectionItemElChild) => {
  var _sectionItem$tooltip, _sectionItem$tooltip2;
  let {
    item
  }: {
    item: WrappedSectionItem;
  } = _ref;
  const sectionItem = item.wrapped;
  // for items with nested sections, avoid displaying the tooltip on hover
  if (!((_sectionItem$tooltip = sectionItem.tooltip) !== null && _sectionItem$tooltip !== void 0 && _sectionItem$tooltip.text) || sectionItem.subSections && !sectionItem.isDisabled) {
    return sectionItemElChild;
  }
  return <>
      <ExperimentalTooltipEl attrs={_objectSpread({
      placement: Placement.Right
    }, (_sectionItem$tooltip2 = sectionItem.tooltip) !== null && _sectionItem$tooltip2 !== void 0 ? _sectionItem$tooltip2 : {})} params={{
      text: sectionItem.tooltip.text
    }} data-sentry-element="ExperimentalTooltipEl" data-sentry-source-file="index.tsx" />
      <section sel='.list-option-wrapper'>{sectionItemElChild}</section>
    </>;
};
export const SectionRowEl = (_ref2: {
  allowPointerEventsAlways: boolean;
  item: WrappedSectionItem;
  isFocused: boolean;
  selectedTheme: SelectedTheme;
  contextPanelTransform: SectionItemContextPanelTransform;
  onClick: ItemAndEventHandler;
  onMouseEnter: ItemHandler;
}, sectionItemElChild?: VNode) => {
  let {
    allowPointerEventsAlways,
    item,
    isFocused,
    selectedTheme,
    contextPanelTransform,
    onClick,
    onMouseEnter
  }: {
    allowPointerEventsAlways: boolean;
    item: WrappedSectionItem;
    isFocused: boolean;
    selectedTheme: SelectedTheme;
    contextPanelTransform: SectionItemContextPanelTransform;
    onClick: ItemAndEventHandler;
    onMouseEnter: ItemHandler;
  } = _ref2;
  const sectionItem = item.wrapped;
  const hasSubSections = Boolean(sectionItem.subSections && sectionItem.subSections.length);
  const isClickEnabled = !(sectionItem.isDisabled || sectionItem.isReadOnly);
  return <div class={{
    highlight: sectionItem.highlightItem
  }} attrs={{
    elref: "itemsMenuSectionRow"
  }} data-sentry-component="SectionRowEl" data-sentry-source-file="index.tsx">
      <li sel='.list-option' class={_objectSpread(_objectSpread({}, !sectionItem.expansionToggle && {
      ["for-".concat(selectedTheme, "-selected-theme")]: true
    }), {}, {
      disabled: sectionItem.isDisabled,
      'special-action': sectionItem.isSpecialAction,
      expander: sectionItem.isExpander,
      focused: isFocused,
      'read-only': sectionItem.isReadOnly,
      selectable: !(sectionItem.isDisabled || sectionItem.isReadOnly),
      selected: sectionItem.isSelected,
      'has-tooltip': Boolean(sectionItem.tooltip && sectionItem.tooltip.text),
      'allow-pointer-events': Boolean(allowPointerEventsAlways || contextPanelTransform),
      'has-sub-sections': hasSubSections,
      ["label-theme-".concat(sectionItem.labelTheme)]: Boolean(sectionItem.labelTheme)
    })} attrs={{
      elref: sectionItem.elref,
      role: "listitem"
    }} on={{
      click: isClickEnabled && (ev => onClick(item, ev)),
      mouseenter: () => onMouseEnter(item)
    }}>
        <TooltipWrapper item={item} data-sentry-element="TooltipWrapper" data-sentry-source-file="index.tsx">{sectionItemElChild}</TooltipWrapper>
      </li>
    </div>;
};
export const SectionEl = (_ref3: {
  section: WrappedSection;
  hideSectionTitle?: boolean;
  areSectionItemsHidden?: boolean;
  numOriginalItems: number;
  i18n: LocalizationStore;
}, sectionItemElChildren?: VNode[]) => {
  let {
    section,
    hideSectionTitle = false,
    areSectionItemsHidden = false,
    numOriginalItems,
    i18n
  }: {
    section: WrappedSection;
    hideSectionTitle?: boolean;
    areSectionItemsHidden?: boolean;
    numOriginalItems: number;
    i18n: LocalizationStore;
  } = _ref3;
  const {
    sectionLabelIconRenderer,
    label,
    tooltip,
    icon,
    isLoading
  } = section;
  const {
    print
  } = i18n;
  const sectionHasHeader = Boolean(!hideSectionTitle && label);
  return <div sel='.section-container' class={{
    'has-expansion-toggle': Boolean(section.expansionToggle),
    'has-header': sectionHasHeader
  }} data-sentry-component="SectionEl" data-sentry-source-file="index.tsx">
      {sectionHasHeader && <div sel='.section-header'>
          <span sel='.label'>{print(label)}</span>
          {isFunction(sectionLabelIconRenderer) && sectionLabelIconRenderer(unwrapSection(section))}
          {icon ? <div sel='.section-icon.item-icon' attrs={{
        icon
      }}>
              {tooltip && <ExperimentalTooltipEl attrs={_objectSpread({
          placement: Placement.RightCenter
        }, tooltip !== null && tooltip !== void 0 ? tooltip : {})} params={{
          text: tooltip.text
        }} />}
              <SvgIconEl attrs={{
          icon,
          empty: !icon
        }} />
            </div> : null}
        </div>}
      {isLoading ? <div sel='.loading-items'>
          <SpinnerEl attrs={{
        elref: "itemsMenuLoadingSpinner"
      }} />
        </div> : <ul sel='.screen-options-list' attrs={{
      role: "list"
    }}>
          {sectionItemElChildren}
        </ul>}
      {!areSectionItemsHidden && !numOriginalItems && section.emptySectionRenderer && <div sel='.empty-section-renderer'>{section.emptySectionRenderer()}</div>}
    </div>;
};